import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, ClipboardList, Phone, ShieldCheck, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { ManagedUser, RefereeRegistration, RegistrationStatus } from "@/types";
import { Button } from "@/components/ui/Button";
import { Card, Badge } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { TableSkeleton } from "@/components/ui/Skeleton";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { Pagination } from "@/components/ui/Pagination";
import { Input } from "@/components/ui/Input";
import { ContactButtons } from "@/components/ContactButtons";
import { ContactRefereeModal } from "@/components/ContactRefereeModal";

interface PaginatedResult<T> {
  data: T[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type RefereeSortKey = "nombre" | "active";
type RegistrationSortKey = "createdAt" | "nombre" | "status";

const selectClass =
  "w-full rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

const dateFormatter = new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function SortHeader({ label, active, dir, onClick }: { label: string; active: boolean; dir: "asc" | "desc"; onClick: () => void }) {
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 font-semibold transition-colors hover:text-ink-700 ${active ? "text-ink-700" : ""}`}
    >
      {label}
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

// Gestión de árbitros (solo super-admin): cuentas ya activas + solicitudes de registro pendientes.
// Mismo patrón de dos pestañas que ProvidersPage.
export function RefereesPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"arbitros" | "registros">("arbitros");

  // --- Árbitros (cuentas) ---
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [nombreInput, setNombreInput] = useState("");
  const [apellidosInput, setApellidosInput] = useState("");
  const [nombreFilter, setNombreFilter] = useState("");
  const [apellidosFilter, setApellidosFilter] = useState("");
  const [activoFilter, setActivoFilter] = useState<"" | "true" | "false">("");
  const [sort, setSort] = useState<{ key: RefereeSortKey; dir: "asc" | "desc" }>({ key: "nombre", dir: "asc" });
  const [confirmReferee, setConfirmReferee] = useState<ManagedUser | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setNombreFilter(nombreInput.trim());
      setApellidosFilter(apellidosInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [nombreInput, apellidosInput]);

  const { data: refereesResp, isLoading } = useQuery({
    queryKey: ["referees", page, limit, nombreFilter, apellidosFilter, activoFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: String(limit), role: "REFEREE" });
      if (nombreFilter) params.set("nombre", nombreFilter);
      if (apellidosFilter) params.set("apellidos", apellidosFilter);
      if (activoFilter) params.set("active", activoFilter);
      return (await api.get<PaginatedResult<ManagedUser>>(`/users?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });
  const referees = refereesResp?.data ?? [];

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => api.put(`/users/${id}`, { active }),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["referees"] });
      toast.success(vars.active ? "Árbitro activado" : "Árbitro desactivado");
    },
    onError: () => toast.error("No se pudo actualizar el árbitro"),
  });

  const visible = useMemo(() => {
    const dirMult = sort.dir === "asc" ? 1 : -1;
    return [...referees].sort((a, b) => {
      if (sort.key === "nombre") return a.nombre.localeCompare(b.nombre) * dirMult;
      return (Number(a.active) - Number(b.active)) * dirMult;
    });
  }, [referees, sort]);

  // --- Registros ---
  const [regPage, setRegPage] = useState(1);
  const [regLimit, setRegLimit] = useState(10);
  const [regNombreInput, setRegNombreInput] = useState("");
  const [regNombreFilter, setRegNombreFilter] = useState("");
  const [regStatus, setRegStatus] = useState<"" | RegistrationStatus>("");
  const [regSort, setRegSort] = useState<{ key: RegistrationSortKey; dir: "asc" | "desc" }>({ key: "createdAt", dir: "desc" });
  const [contacting, setContacting] = useState<RefereeRegistration | null>(null);
  const [approving, setApproving] = useState<RefereeRegistration | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setRegNombreFilter(regNombreInput.trim());
      setRegPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [regNombreInput]);

  const { data: regResp, isLoading: regLoading } = useQuery({
    queryKey: ["referee-registrations", regPage, regLimit, regNombreFilter, regStatus],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(regPage), limit: String(regLimit) });
      if (regNombreFilter) params.set("nombre", regNombreFilter);
      if (regStatus) params.set("status", regStatus);
      return (await api.get<PaginatedResult<RefereeRegistration>>(`/referee-registrations?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });
  const registrations = regResp?.data ?? [];

  const { data: pendingCountRaw } = useQuery({
    queryKey: ["referee-registrations", "pendingCount"],
    queryFn: async () =>
      (await api.get<PaginatedResult<RefereeRegistration>>("/referee-registrations?status=PENDING&limit=1")).data.total,
  });
  const pendingCount = pendingCountRaw ?? 0;

  const approve = useMutation({
    mutationFn: (id: number) => api.post(`/referee-registrations/${id}/approve`),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ["referee-registrations"] });
      queryClient.invalidateQueries({ queryKey: ["referees"] });
      toast.success(
        data.emailSent
          ? "Registro validado: se creó la cuenta de árbitro y se envió el correo de acceso."
          : "Registro validado: se creó la cuenta de árbitro, pero no se pudo enviar el correo de acceso."
      );
    },
    onError: (error: any) => toast.error(error?.response?.data?.error ?? "No se pudo validar el registro"),
  });

  const visibleRegistrations = useMemo(() => {
    const dirMult = regSort.dir === "asc" ? 1 : -1;
    return [...registrations].sort((a, b) => {
      if (regSort.key === "createdAt") return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * dirMult;
      if (regSort.key === "nombre") return a.nombre.localeCompare(b.nombre) * dirMult;
      return a.status.localeCompare(b.status) * dirMult;
    });
  }, [registrations, regSort]);

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: "Árbitros" }]} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
            {tab === "arbitros" ? <UserCheck className="h-6 w-6" /> : <ClipboardList className="h-6 w-6" />}
          </span>
          <div>
            <h1 className="text-2xl font-bold text-ink-900">Árbitros</h1>
            <p className="text-ink-400">
              {tab === "arbitros"
                ? "Cuentas de árbitro activas en la plataforma."
                : "Solicitudes de registro de árbitros pendientes de revisar."}
            </p>
          </div>
        </div>
        <div className="flex rounded-xl border border-ink-100 bg-white p-1">
          <button
            onClick={() => setTab("arbitros")}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
              tab === "arbitros" ? "bg-brand-500 text-white" : "text-ink-500 hover:bg-ink-50"
            }`}
          >
            <UserCheck className="h-3.5 w-3.5" />
            Árbitros
          </button>
          <button
            onClick={() => setTab("registros")}
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
              tab === "registros" ? "bg-brand-500 text-white" : "text-ink-500 hover:bg-ink-50"
            }`}
          >
            <ClipboardList className="h-3.5 w-3.5" />
            Registros
            {pendingCount > 0 && (
              <span
                className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] ${
                  tab === "registros" ? "bg-white/20 text-white" : "bg-brand-100 text-brand-700"
                }`}
              >
                {pendingCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {tab === "arbitros" ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Input label="Nombre" placeholder="Buscar por nombre…" value={nombreInput} onChange={(e) => setNombreInput(e.target.value)} className="w-full" />
            <Input label="Apellidos" placeholder="Buscar por apellidos…" value={apellidosInput} onChange={(e) => setApellidosInput(e.target.value)} className="w-full" />
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink-600">Estado</label>
              <select
                value={activoFilter}
                onChange={(e) => {
                  setActivoFilter(e.target.value as "" | "true" | "false");
                  setPage(1);
                }}
                className={selectClass}
              >
                <option value="">Todos</option>
                <option value="true">Activos</option>
                <option value="false">Inactivos</option>
              </select>
            </div>
          </div>

          <Card className="overflow-hidden p-0">
            {isLoading ? (
              <TableSkeleton rows={3} cols={5} />
            ) : !visible.length ? (
              <p className="p-6 text-sm text-ink-400">
                {nombreFilter || apellidosFilter || activoFilter ? "Ningún árbitro coincide con los filtros." : "Todavía no hay árbitros validados."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full table-fixed text-left text-sm">
                  <thead className="bg-ink-50/60 text-xs tracking-wide text-ink-400 uppercase">
                    <tr>
                      <th className="px-4 py-3 sm:px-6">
                        <SortHeader
                          label="Nombre"
                          active={sort.key === "nombre"}
                          dir={sort.dir}
                          onClick={() => setSort((s) => (s.key === "nombre" ? { key: "nombre", dir: s.dir === "asc" ? "desc" : "asc" } : { key: "nombre", dir: "asc" }))}
                        />
                      </th>
                      <th className="hidden px-4 py-3 font-semibold md:table-cell md:w-[20%] lg:px-6">Apellidos</th>
                      <th className="hidden px-4 py-3 font-semibold md:table-cell md:w-[34%] lg:px-6">Email</th>
                      <th className="hidden px-4 py-3 sm:table-cell sm:w-28 lg:px-6">
                        <SortHeader
                          label="Estado"
                          active={sort.key === "active"}
                          dir={sort.dir}
                          onClick={() => setSort((s) => (s.key === "active" ? { key: "active", dir: s.dir === "asc" ? "desc" : "asc" } : { key: "active", dir: "asc" }))}
                        />
                      </th>
                      <th className="w-28 px-3 py-3 font-semibold sm:w-32 sm:px-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r) => (
                      <tr key={r.id} className="border-t border-ink-100 transition-colors hover:bg-ink-50/50">
                        <td className="px-4 py-3.5 sm:px-6">
                          <p className="truncate font-medium text-ink-800" title={`${r.nombre} ${r.apellidos ?? ""}`}>
                            {r.nombre}
                            <span className="md:hidden"> {r.apellidos}</span>
                          </p>
                          {/* En pantallas estrechas email y estado van bajo el nombre */}
                          <p className="truncate text-xs text-ink-400 md:hidden">{r.email}</p>
                          <div className="mt-1 flex items-center gap-2 sm:hidden">
                            <Badge tone={r.active ? "green" : "red"}>{r.active ? "Activo" : "Inactivo"}</Badge>
                            <ContactButtons telefono={r.telefono} email={r.email} />
                          </div>
                        </td>
                        <td className="hidden truncate px-4 py-3.5 text-ink-600 md:table-cell lg:px-6">{r.apellidos ?? "—"}</td>
                        <td className="hidden px-4 py-3.5 text-ink-400 md:table-cell lg:px-6">
                          <span className="flex min-w-0 items-center gap-2">
                            <span className="truncate" title={r.email}>
                              {r.email}
                            </span>
                            <ContactButtons telefono={r.telefono} email={r.email} />
                          </span>
                        </td>
                        <td className="hidden px-4 py-3.5 sm:table-cell lg:px-6">
                          <Badge tone={r.active ? "green" : "red"}>{r.active ? "Activo" : "Inactivo"}</Badge>
                        </td>
                        <td className="px-3 py-3.5 text-right sm:px-4">
                          <Button className="w-24" onClick={() => setConfirmReferee(r)}>
                            {r.active ? "Desactivar" : "Activar"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          <Pagination
            page={refereesResp?.page ?? 1}
            totalPages={refereesResp?.totalPages ?? 1}
            total={refereesResp?.total ?? 0}
            onPageChange={setPage}
            limit={limit}
            onLimitChange={(l) => {
              setLimit(l);
              setPage(1);
            }}
          />
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Input label="Nombre" placeholder="Buscar por nombre…" value={regNombreInput} onChange={(e) => setRegNombreInput(e.target.value)} className="w-full" />
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink-600">Estado</label>
              <select
                value={regStatus}
                onChange={(e) => {
                  setRegStatus(e.target.value as "" | RegistrationStatus);
                  setRegPage(1);
                }}
                className={selectClass}
              >
                <option value="">Todos</option>
                <option value="PENDING">Pendiente</option>
                <option value="CONTACTED">Contactado</option>
                <option value="APPROVED">Aprobado</option>
              </select>
            </div>
          </div>

          <Card className="overflow-hidden p-0">
            {regLoading ? (
              <TableSkeleton rows={4} cols={5} />
            ) : !visibleRegistrations.length ? (
              <p className="p-6 text-sm text-ink-400">
                {regNombreFilter || regStatus ? "Ningún registro coincide con los filtros." : "Todavía no hay solicitudes de registro."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full table-fixed text-left text-sm">
                  <thead className="bg-ink-50/60 text-xs tracking-wide text-ink-400 uppercase">
                    <tr>
                      <th className="px-4 py-3 sm:px-6">
                        <SortHeader
                          label="Árbitro"
                          active={regSort.key === "nombre"}
                          dir={regSort.dir}
                          onClick={() => setRegSort((s) => (s.key === "nombre" ? { key: "nombre", dir: s.dir === "asc" ? "desc" : "asc" } : { key: "nombre", dir: "asc" }))}
                        />
                      </th>
                      <th className="hidden px-4 py-3 font-semibold md:table-cell md:w-[30%] lg:px-6">Contacto</th>
                      <th className="hidden px-4 py-3 xl:table-cell xl:w-36">
                        <SortHeader
                          label="Recibido"
                          active={regSort.key === "createdAt"}
                          dir={regSort.dir}
                          onClick={() => setRegSort((s) => (s.key === "createdAt" ? { key: "createdAt", dir: s.dir === "asc" ? "desc" : "asc" } : { key: "createdAt", dir: "asc" }))}
                        />
                      </th>
                      <th className="hidden px-4 py-3 sm:table-cell sm:w-32">
                        <SortHeader
                          label="Estado"
                          active={regSort.key === "status"}
                          dir={regSort.dir}
                          onClick={() => setRegSort((s) => (s.key === "status" ? { key: "status", dir: s.dir === "asc" ? "desc" : "asc" } : { key: "status", dir: "asc" }))}
                        />
                      </th>
                      <th className="w-32 px-3 py-3 font-semibold sm:px-4 lg:w-72"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRegistrations.map((r) => (
                      <tr key={r.id} className="border-t border-ink-100 transition-colors hover:bg-ink-50/50">
                        <td className="px-4 py-3.5 sm:px-6">
                          <p className="truncate font-medium text-ink-800" title={`${r.nombre} ${r.apellidos ?? ""}`}>
                            {r.nombre} {r.apellidos}
                          </p>
                          {r.mensaje && <p className="truncate text-xs text-ink-400">{r.mensaje}</p>}
                          {/* En pantallas estrechas contacto y estado van bajo el nombre */}
                          <p className="truncate text-xs text-ink-400 md:hidden">
                            {r.email} · {r.telefono}
                          </p>
                          <div className="mt-1 sm:hidden">
                            <Badge tone={r.status === "APPROVED" ? "green" : "slate"}>
                              {r.status === "APPROVED" ? "Aprobado" : r.status === "CONTACTED" ? "Contactado" : "Pendiente"}
                            </Badge>
                          </div>
                        </td>
                        <td className="hidden px-4 py-3.5 text-ink-500 md:table-cell lg:px-6">
                          <p className="truncate" title={r.email}>
                            {r.email}
                          </p>
                          <p className="truncate text-xs text-ink-400">{r.telefono}</p>
                        </td>
                        <td className="hidden px-4 py-3.5 text-ink-500 xl:table-cell">{dateFormatter.format(new Date(r.createdAt))}</td>
                        <td className="hidden px-4 py-3.5 sm:table-cell">
                          <Badge tone={r.status === "APPROVED" ? "green" : "slate"}>
                            {r.status === "APPROVED" ? "Aprobado" : r.status === "CONTACTED" ? "Contactado" : "Pendiente"}
                          </Badge>
                        </td>
                        <td className="px-3 py-3.5 text-right sm:px-4">
                          <div className="flex flex-col items-stretch gap-2 lg:flex-row lg:justify-end">
                            <Button onClick={() => setContacting(r)}>
                              <Phone className="h-3.5 w-3.5" />
                              Contactar
                            </Button>
                            <Button className="w-28" onClick={() => setApproving(r)} disabled={r.status === "APPROVED"}>
                              <ShieldCheck className="h-3.5 w-3.5" />
                              {r.status === "APPROVED" ? "Validado" : "Validar"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          <Pagination
            page={regResp?.page ?? 1}
            totalPages={regResp?.totalPages ?? 1}
            total={regResp?.total ?? 0}
            onPageChange={setRegPage}
            limit={regLimit}
            onLimitChange={(l) => {
              setRegLimit(l);
              setRegPage(1);
            }}
          />
        </>
      )}

      {confirmReferee && (
        <ConfirmDialog
          open={!!confirmReferee}
          onOpenChange={(open) => !open && setConfirmReferee(null)}
          title={confirmReferee.active ? "Desactivar árbitro" : "Activar árbitro"}
          description={`¿Seguro que quieres ${confirmReferee.active ? "desactivar" : "activar"} a "${confirmReferee.nombre}"? ${
            confirmReferee.active ? "No podrá iniciar sesión ni se le podrá asignar a nuevos partidos." : ""
          }`}
          tone="primary"
          confirmLabel={confirmReferee.active ? "Desactivar" : "Activar"}
          onConfirm={() => toggleActive.mutate({ id: confirmReferee.id, active: !confirmReferee.active })}
        />
      )}

      {contacting && (
        <ContactRefereeModal registration={contacting} open={!!contacting} onOpenChange={(open) => !open && setContacting(null)} />
      )}

      {approving && (
        <ConfirmDialog
          open={!!approving}
          onOpenChange={(open) => !open && setApproving(null)}
          title="Validar registro de árbitro"
          description={`Se creará la cuenta de árbitro de "${approving.nombre}" con el email y la contraseña que indicó al registrarse, y se le avisará por correo de que ya puede iniciar sesión.`}
          tone="primary"
          confirmLabel="Validar"
          onConfirm={() => approve.mutate(approving.id)}
        />
      )}
    </div>
  );
}
